import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Error404Page } from '../Error404Page';
import { BuildingActions, SuiteActions, NetworkActions } from '../../store';
import { ConnectedMainDisplayBoxWithRouter } from '../MainDisplayBox';
import { ConnectedLegendWithRouter } from './components';
import { ConnectedThermostatDetailsWithRouter } from './components';
import './SuiteGraphPage.css';
import * as util from '../../utils/urlParams';
import { Location } from 'history';
import { SelectedBuilding, SubCategoryType, NavigationParamsType } from '../../store/building/types';
import { SuiteData, GraphDateRange } from '../../store/suite/types';

export interface Props {
  clearErrors(): void;
  clearSuiteData(): void;
  getSuiteList(building: string, category: string, thermostat: string): void;
  invalidSelectedBuildingError: string;
  isLoading: boolean;
  loadSuiteGraphPage(parameters: {
    buildingSlug: any;
    subcategory: any;
    uid: any;
    dataPoints?: any;
    graphDateRange: GraphDateRange;
  }): void;
  location: Location;
  selectedBuilding: SelectedBuilding;
  navigationParams: NavigationParamsType;
  selectedSuite: {
    value: string;
    label: string;
  };
  selectedSuiteDataPoints: {
    api_location: string;
    id: string;
    name: string;
    status: string;
  }[];
  setSelectedBuilding(building: string): void;
  suiteData: SuiteData;
  graphDateRange: GraphDateRange;
  updateGraphDateRange(filter: GraphDateRange): void;
  clearLastRequest(): void;
}
export declare type ComposedProps = Props & RouteComponentProps;

export class SuiteGraphPage extends Component<ComposedProps, any> {
  componentDidMount() {
    const {
      getSuiteList,
      loadSuiteGraphPage,
      location: { search },
      updateGraphDateRange,
      setSelectedBuilding,
      clearSuiteData,
      selectedBuilding: { identifier },
    } = this.props;

    // TO DO: move to a parent component
    clearSuiteData();
    setSelectedBuilding(identifier);

    let filter: GraphDateRange =
      util.getThermostatsFilterFromUrlParams(search) || (this.getDateSearch() as GraphDateRange);
    const thermostat = util.getThermostatFromUrlParams(search) as string;
    const dataPoints = util.getDataPointsFromUrlParams(search);
    const subcategory = 'suite_graphs';
    getSuiteList(identifier, subcategory, thermostat);

    if (filter) {
      updateGraphDateRange(filter);
    }

    if (thermostat) {
      loadSuiteGraphPage({
        buildingSlug: identifier,
        subcategory,
        uid: thermostat,
        dataPoints,
        graphDateRange: filter,
      });
    }
  }

  async componentDidUpdate(prevProps: Props) {
    const {
      location: { search: prevSearch },
      selectedSuite: { value: prevSelectedSuite },
    } = prevProps;

    const {
      loadSuiteGraphPage,
      location: { search: newSearch },
      selectedBuilding: { identifier: newBuilding },
      updateGraphDateRange,
      selectedSuite: { value: newSelectedSuite },
      navigationParams: { selectedSubCategory },
    } = this.props;

    const subcategory = 'suite_graphs';
    let newThermostat = util.getThermostatFromUrlParams(newSearch);
    let newDataPoints = util.getDataPointsFromUrlParams(newSearch) as string[];
    let newFilter = util.getThermostatsFilterFromUrlParams(newSearch) || (this.getDateSearch() as GraphDateRange);

    if (!prevSelectedSuite && prevSelectedSuite !== newSelectedSuite) {
      const searchWithThermostat = util.addThermostatSearchParams(newFilter, newSelectedSuite, newDataPoints);

      if (newSearch !== `?${searchWithThermostat}`) {
        this.props.history.replace(`${selectedSubCategory.url}?${searchWithThermostat}`);

        newThermostat = util.getThermostatFromUrlParams(searchWithThermostat);
        newDataPoints = util.getDataPointsFromUrlParams(searchWithThermostat) as string[];
        newFilter =
          util.getThermostatsFilterFromUrlParams(searchWithThermostat) || (this.getDateSearch() as GraphDateRange);
      }

      return loadSuiteGraphPage({
        buildingSlug: newBuilding,
        subcategory,
        uid: newThermostat,
        dataPoints: newDataPoints,
        graphDateRange: newFilter,
      });
    }

    // Resend the api request to get data only if the location props updated
    if (prevSearch !== newSearch && newThermostat) {
      updateGraphDateRange(newFilter);
      return loadSuiteGraphPage({
        buildingSlug: newBuilding,
        subcategory,
        uid: newThermostat,
        dataPoints: newDataPoints,
        graphDateRange: newFilter,
      });
    }
  }

  componentWillUnmount() {
    this.props.clearLastRequest();
    this.props.clearErrors();
  }

  getDateSearch() {
    const { graphDateRange } = this.props;

    const thermostatDateFromSession = window.sessionStorage.getItem('thermostatGraphDateRange');
    const thermostatGraphDateRange = thermostatDateFromSession ? JSON.parse(thermostatDateFromSession) : graphDateRange;

    return thermostatGraphDateRange;
  }

  render() {
    const { invalidSelectedBuildingError, graphDateRange, updateGraphDateRange, selectedSuite } = this.props;

    if (invalidSelectedBuildingError) {
      return <Error404Page />;
    }

    const { isLoading } = this.props;

    if (isLoading || !selectedSuite.value) {
      return (
        <React.Fragment>
          <ConnectedMainDisplayBoxWithRouter isLoading={true} />
          <div className="suite-graph__info-section--loading" />
        </React.Fragment>
      );
    }

    const {
      selectedBuilding: { name, savings_start_datetime: baselineStartDate },
      suiteData: { date, point_data: pointData, report_unit: reportUnit },
      selectedSuiteDataPoints,
      navigationParams: { selectedSubCategory },
    } = this.props;
    const { api_location: apiLocation } = selectedSubCategory;

    return (
      <React.Fragment>
        <ConnectedMainDisplayBoxWithRouter
          apiLocation={apiLocation}
          building={name}
          date={date}
          graphData={pointData}
          name="Thermostat"
          projectStartDate={baselineStartDate}
          reportUnit={reportUnit}
          graphDateRange={graphDateRange}
          updateGraphDateRange={updateGraphDateRange}
        />
        <div className="suite-graph__info-section">
          <ConnectedThermostatDetailsWithRouter />
          <ConnectedLegendWithRouter displayedItems={selectedSuiteDataPoints} />
        </div>
      </React.Fragment>
    );
  }
}

export function mapStateToProps(state: any) {
  return {
    isLoading: state.suiteReducer.isFetching,
    graphDateRange: state.suiteReducer.graphDateRange,
    invalidSelectedBuildingError: state.buildingReducer.error,
    selectedBuilding: state.buildingReducer.selectedBuilding,
    navigationParams: state.buildingReducer.navigationParams,
    selectedSuite: state.suiteReducer.selectedSuite,
    suiteData: state.suiteReducer.suiteData,
    selectedSuiteDataPoints: state.suiteReducer.selectedSuiteDataPoints,
  };
}

const mapDispatchToProps = {
  clearErrors: SuiteActions.clearErrors,
  clearLastRequest: NetworkActions.clearLastRequest,
  getSuiteList: SuiteActions.getSuiteList,
  loadSuiteGraphPage: SuiteActions.loadSuiteGraphPage,
  clearSuiteData: SuiteActions.clearSuiteData,
  setSelectedBuilding: SuiteActions.setSelectedBuilding,
  updateGraphDateRange: SuiteActions.updateGraphDateRange,
};

const SuiteGraphPageWithRouter = withRouter<ComposedProps>(SuiteGraphPage);
export const ConnectedSuiteGraphPageWithRouter = connect(mapStateToProps, mapDispatchToProps)(SuiteGraphPageWithRouter);
